import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { extractError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

const EMPTY = { name: "", email: "", phone: "", role: "manager", password: "" };

export default function Team() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [createdPwd, setCreatedPwd] = useState(null);

  const load = async () => {
    const { data } = await api.get("/team");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/team", form);
      toast.success("Team member added");
      setForm(EMPTY);
      if (data.initial_password) setCreatedPwd({ email: data.email, password: data.initial_password });
      else setOpen(false);
      load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const setActive = async (id, status) => {
    try {
      await api.patch(`/team/${id}`, { status });
      toast.success("Updated");
      load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this team member?")) return;
    try {
      await api.delete(`/team/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(extractError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">People</div>
          <h1 className="font-serif text-4xl tracking-tighter mt-1 text-[#1A1A1A]">Team</h1>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(EMPTY); setCreatedPwd(null); } }}>
          <DialogTrigger asChild>
            <Button className="bg-[#1A1A1A] text-white hover:bg-black rounded-full" data-testid="team-add-btn"><Plus size={16} className="mr-1" /> Add member</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{createdPwd ? "Share credentials" : "Add team member"}</DialogTitle></DialogHeader>
            {createdPwd ? (
              <div className="space-y-3" data-testid="team-credentials">
                <p className="text-sm">Send these credentials to your new team member:</p>
                <div className="font-mono text-xs bg-zinc-50 border border-black/10 p-3 rounded">
                  <div>Email: <span className="font-medium">{createdPwd.email}</span></div>
                  <div>Temp password: <span className="font-medium">{createdPwd.password}</span></div>
                </div>
                <DialogFooter>
                  <Button onClick={() => { setOpen(false); setCreatedPwd(null); }} className="bg-[#1A1A1A] text-white hover:bg-black">Done</Button>
                </DialogFooter>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="team-form">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Name *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-name-input" /></div>
                  <div><Label>Email *</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="team-email-input" /></div>
                  <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                  <div><Label>Role</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="team-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="employee">Employee</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Label>Password (optional — defaults to Welcome@123)</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-[#D85C38] hover:bg-[#B84A2A] text-white" data-testid="team-save-btn">Add member</Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-black/10 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {items.map((u) => (
              <TableRow key={u.id} data-testid={`team-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-zinc-600">{u.email}</TableCell>
                <TableCell className="capitalize">{u.role}</TableCell>
                <TableCell><Badge variant="outline" className={u.status === "active" ? "bg-emerald-100 text-emerald-900 border-emerald-200" : "bg-zinc-100 text-zinc-700"}>{u.status}</Badge></TableCell>
                <TableCell className="text-right space-x-2">
                  <Button variant="ghost" size="sm" onClick={() => setActive(u.id, u.status === "active" ? "inactive" : "active")} data-testid={`team-toggle-${u.id}`}>
                    {u.status === "active" ? "Deactivate" : "Activate"}
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-700 hover:bg-red-50" onClick={() => del(u.id)} data-testid={`team-delete-${u.id}`}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
